import { useEffect } from 'react';
import { Controller, type DefaultValues, type SubmitHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { dealerBankAccountSchema, type DealerBankAccountFormInput } from '../schemas/dealerBankAccount.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import { Checkbox } from '@/shared/components/form';

interface DealerBankAccountFormProps {
  onSubmit: SubmitHandler<DealerBankAccountFormInput>;
  onCancel?: () => void;
  isLoading?: boolean;
  onSetErrorRef?: (setError: UseFormSetError<DealerBankAccountFormInput>) => void;
}

export function DealerBankAccountForm({ onSubmit, onCancel, isLoading = false, onSetErrorRef }: DealerBankAccountFormProps) {
  const { handleSubmit, control, setError, formState: { isSubmitting } } = useForm<DealerBankAccountFormInput>({
    resolver: zodResolver(dealerBankAccountSchema),
    defaultValues: {
      account_name: '',
      account_number: '',
      sort_code: '',
      bank_name: '',
      is_primary: false,
    } as DefaultValues<DealerBankAccountFormInput>,
  });

  useEffect(() => { onSetErrorRef?.(setError); }, [onSetErrorRef, setError]);

  return (
    <Form className="space-y-4 max-w-lg" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="account_name">Account Name</Label>
        <Controller
          name="account_name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="account_name" placeholder="e.g. Main Trading Account" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="account_number">Account Number</Label>
        <Controller
          name="account_number"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="account_number" placeholder="8 digits" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="sort_code">Sort Code</Label>
        <Controller
          name="sort_code"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="sort_code" placeholder="6 digits, no dashes" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Label htmlFor="bank_name">Bank Name <span className="text-mp-muted">(optional)</span></Label>
        <Controller
          name="bank_name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input id="bank_name" placeholder="e.g. Barclays" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
          )}
        />
      </div>

      <div>
        <Controller
          name="is_primary"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="is_primary"
              checked={field.value ?? false}
              onChange={field.onChange}
              label="Set as primary account"
              labelClassName="text-sm"
              ariaLabel="Set this as primary bank account"
              disabled={isLoading || isSubmitting}
            />
          )}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button size="sm" disabled={isLoading || isSubmitting}>
          {isLoading || isSubmitting ? 'Saving...' : 'Add Bank Account'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={isLoading || isSubmitting}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}
